import React, { useEffect, useRef, useState } from 'react'
import GenerateIcon from './GenerateIcon'
import api, { handleRequestError } from '../services/api'
import { useDispatch } from 'react-redux'
import Thumbnail from './Thumbnail'
import { IconButton, Skeleton } from '@mui/material'
import AlertElement from './AlertElement'

export interface IAttachment {
    id: number,
    img: string
}

interface IAttachmentProps extends IAttachment {
    disableDelete?: boolean,
    handleDeleteUpload: (id: number) => void,
}

export function Attachment(props: IAttachmentProps){

    const {
        id,
        img,
        disableDelete,
        handleDeleteUpload,
    } = props

    return(
        <div className="single-attachment-box">
            <div className="content-of-single-attachment-box">
                {/* <div className="single-attachment-box" style={{ backgroundImage: `url(${img})`, backgroundSize: "cover" }}></div> */}

                {!disableDelete && (
                    <div className="delete-box-of-single-attachment-box">
                        <div className="content-of-delete-box-of-single-attachment-box">
                            <IconButton color="error" onClick={() => handleDeleteUpload(id)}>
                                <GenerateIcon icon="delete" />
                            </IconButton>
                        </div>
                    </div>
                )}

                <Thumbnail src={img} />
            </div>
        </div>
    )
}

interface IAttachmentsProps {
    label?: string,
    attachments: IAttachment[],
    setAttachments: (attachments: IAttachment[]) => void,
    disableUpload?: boolean,
    disableDelete?: boolean,
    maxAttachments?: number,
    maxAttachmentsAtOnce?: number,
    showNothingMessage?: boolean,
}

export default function Attachments(props: IAttachmentsProps) {

    const dispatch = useDispatch()

    const { 
        label,
        attachments,
        setAttachments,
        disableUpload,
        disableDelete,
        maxAttachments,
        showNothingMessage,
    } = props

    const fileInputRef = useRef<HTMLInputElement>(null)

    const maxReached = maxAttachments !== undefined && attachments.length >= maxAttachments
    const maxAttachmentsAtOnce = props.maxAttachmentsAtOnce ? props.maxAttachmentsAtOnce : 10

    const [loading, setLoading] = useState<boolean>(false)

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files
        if (!files || files.length === 0 || maxReached) return

        if (maxAttachmentsAtOnce){
            if (files.length > maxAttachmentsAtOnce){
                handleRequestError({ response: { data: { message: `You can only upload ${maxAttachments} images at one time.` } } }, dispatch)
                return;
            }
        }

        setLoading(true)

        const fileArray = Array.from(files)

        try {
            const uploadPromises = fileArray.map(file => {
                const formData = new FormData()
                formData.append('image', file)

                return api.post("/uploader/image", formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    }
                })
            })

            const responses = await Promise.all(uploadPromises)

            const newAttachments = responses.map(response => ({
                id: response.data.id,
                img: response.data.url,
            }))

            // ✅ use functional update to avoid stale attachments
            setAttachments([
                ...attachments,
                ...newAttachments
            ])

            
        } catch (error: any) {
            handleRequestError(error, dispatch)
        } finally {
            setLoading(false)
            if (fileInputRef.current) fileInputRef.current.value = ''
        }

        /**
         * Reset input
         */
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const handleDeleteUpload = (id: number) => {
        let newAttachments = [...attachments]

        if (!newAttachments.find((att) => att.id === id)){
            return;
        }

        setAttachments(newAttachments.filter(att => att.id !== id))
    }

    return(
        <div className="attachments-container">
            <div className="content-of-attachments-container">

                {label && <h4 className="label-of-attachments-container">{label}</h4>}

                <div className="all-attachments-container">
                    <div className="content-of-all-attachments-container">
                        {attachments && attachments.length > 0 && attachments.map((attachment, index: number) => <Attachment {...attachment} key={index} handleDeleteUpload={handleDeleteUpload} disableDelete={disableDelete} />)}

                        {!disableUpload && !maxReached && (
                            <div className="single-attachment-box add-new" onClick={() => {
                                if (!fileInputRef){
                                    return;
                                }

                                fileInputRef.current?.click()

                            }}>
                                <div className="content-of-single-attachment-box">
                                    <GenerateIcon icon="new" />
                                </div>
                            </div>
                        )}

                        {loading && (
                            <div className="single-attachment-box loading">
                                <div className="content-of-single-attachment-box">
                                    <Skeleton variant="rectangular" width={"100%"} height={"100%"} />
                                </div>
                            </div>
                        )}

                        <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            multiple
                        />

                    </div>
                </div>

            </div>
        </div>
    )
}